import React from 'react';

function Pricing() {
  const plans = [
    {
      name: 'Single Office',
      price: 499,
      offices: '1 Office',
      totalCost: '$499',
      features: [
        'Full EzzyDental Platform',
        'Standard Support',
        'Basic Reporting',
        'Cloud Storage',
        'Regular Updates'
      ]
    },
    {
      name: 'Growing Practice',
      price: 599,
      offices: '2-5 Offices',
      totalCost: '$1,198 - $2,995',
      features: [
        'Premium Support',
        'Multi-location Dashboard',
        'Advanced Reporting',
        'Team Training',
        'Priority Updates'
      ]
    },
    {
      name: 'Established DSO',
      price: 650,
      offices: '6-10 Offices',
      totalCost: '$3,900 - $6,500',
      features: [
        'Custom Reporting',
        'Priority Support',
        'Dedicated Account Manager',
        'Custom Integrations',
        'Advanced Security'
      ]
    },
    {
      name: 'Enterprise',
      price: '650+',
      offices: '10+ Offices',
      totalCost: '$6,500+',
      features: [
        'Tailored Features',
        'API Integrations',
        'Custom Development',
        'Enterprise Support',
        'Unlimited Storage'
      ]
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E5F0F3] to-white">
        <div className="container px-6 py-16 mx-auto">
          <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#1E2A4A] mb-6">
                Transparent Pricing for Every Practice Size
              </h1>
              <p className="mb-8 text-xl text-gray-600">
                Flexible pricing plans designed to scale with your dental organization
              </p>
              <button className="px-8 py-3 font-medium text-white transition-colors bg-orange-500 rounded-full hover:bg-orange-600">
                Schedule Demo
              </button>
            </div>
            <div className="relative">
              <img
                src="/assets/office1.png"
                alt="Pricing"
                className="shadow-xl rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/20 to-transparent rounded-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Plans */}
      <div className="container px-6 py-16 mx-auto">
        <h2 className="text-3xl font-bold text-[#1E2A4A] text-center mb-12">
          Pricing Plans for Every Practice Size
        </h2>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan, index) => (
            <div key={index} className="p-6 transition-shadow bg-white shadow-sm rounded-xl hover:shadow-lg">
              <div className="mb-6 text-center">
                <h3 className="text-xl font-bold text-[#1E2A4A] mb-2">{plan.name}</h3>
                <div className="mb-1 text-3xl font-bold text-orange-500">
                  ${plan.price}<span className="text-sm text-gray-500">/office/mo</span>
                </div>
                <div className="mb-2 text-gray-600">{plan.offices}</div>
                <div className="text-sm text-gray-500">Total: {plan.totalCost}</div>
              </div>
              <ul className="space-y-3">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center text-gray-600">
                    <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <button className="w-full px-6 py-3 mt-6 font-medium text-white transition-colors bg-orange-500 rounded-full hover:bg-orange-600">
                Get Started
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Enterprise Features */}
      <div className="py-16 bg-gray-50">
        <div className="container px-6 mx-auto">
          <h2 className="text-3xl font-bold text-[#1E2A4A] text-center mb-12">
            Enterprise Features & Support
          </h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            <div className="p-6 bg-white shadow-sm rounded-xl">
              <h3 className="text-xl font-bold text-[#1E2A4A] mb-4">Custom Onboarding</h3>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-600">
                  <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Dedicated Account Manager
                </li>
                <li className="flex items-center text-gray-600">
                  <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Multi-Location Setup
                </li>
                <li className="flex items-center text-gray-600">
                  <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Data Migration
                </li>
              </ul>
            </div>
            <div className="p-6 bg-white shadow-sm rounded-xl">
              <h3 className="text-xl font-bold text-[#1E2A4A] mb-4">Advanced Features</h3>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-600">
                  <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Custom API Integrations
                </li>
                <li className="flex items-center text-gray-600">
                  <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Advanced Security
                </li>
                <li className="flex items-center text-gray-600">
                  <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Custom Reporting
                </li>
              </ul>
            </div>
            <div className="p-6 bg-white shadow-sm rounded-xl">
              <h3 className="text-xl font-bold text-[#1E2A4A] mb-4">Premium Support</h3>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-600">
                  <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  24/7 Priority Support
                </li>
                <li className="flex items-center text-gray-600">
                  <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Dedicated Support Team
                </li>
                <li className="flex items-center text-gray-600">
                  <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  SLA Guarantees
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="container px-6 py-16 mx-auto">
        <h2 className="text-3xl font-bold text-[#1E2A4A] text-center mb-12">
          Frequently Asked Questions
        </h2>
        <div className="grid max-w-3xl grid-cols-1 gap-8 mx-auto md:grid-cols-2">
          <div className="p-6 bg-white shadow-sm rounded-xl">
            <h3 className="text-lg font-bold text-[#1E2A4A] mb-2">What's included in the base price?</h3>
            <p className="text-gray-600">Each plan includes the full EzzyDental platform, cloud storage, regular updates, and basic support. Higher tiers include additional features and premium support.</p>
          </div>
          <div className="p-6 bg-white shadow-sm rounded-xl">
            <h3 className="text-lg font-bold text-[#1E2A4A] mb-2">Can I upgrade my plan later?</h3>
            <p className="text-gray-600">Yes, you can upgrade your plan at any time. The price will be prorated based on your remaining subscription period.</p>
          </div>
          <div className="p-6 bg-white shadow-sm rounded-xl">
            <h3 className="text-lg font-bold text-[#1E2A4A] mb-2">What's the minimum contract length?</h3>
            <p className="text-gray-600">We offer flexible month-to-month plans with no long-term commitment required. Annual plans are available with additional savings.</p>
          </div>
          <div className="p-6 bg-white shadow-sm rounded-xl">
            <h3 className="text-lg font-bold text-[#1E2A4A] mb-2">Do you offer custom solutions?</h3>
            <p className="text-gray-600">Yes, our Enterprise plan includes custom development, API integrations, and tailored features to meet your specific needs.</p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 text-white bg-orange-500">
        <div className="container px-6 mx-auto text-center">
          <h2 className="mb-6 text-3xl font-bold">Ready to Transform Your Practice?</h2>
          <p className="mb-8 text-xl">Schedule a demo to see how EzzyDental can help your practice grow</p>
          <button className="px-8 py-3 font-medium text-orange-500 transition-colors bg-white rounded-full hover:bg-gray-100">
            Schedule Demo
          </button>
        </div>
      </div>
    </div>
  );
}

export default Pricing; 